import { useCallback, useEffect, useRef, useState } from "react";
import type {
  SlackConversationStatus,
  SlackConversationDelivery as Delivery,
} from "@io-spoke/contracts/slackConversations";
import { getRescueConversationIdentity } from "../../hooks/useRescueConversation";
import { useAsyncAction } from "../../hooks/useAsyncAction";
import {
  getSlackConversationStatus,
  retrySlackConversation,
  sendSlackConversation,
  startSlackConversationIdentity,
} from "../../lib/slackConversations";
import { Button } from "../ui/button";
import { ChoiceSelect } from "../ui/choice-select";

const deliveryCopy = {
  queued: "Queued for Slack",
  sending: "Sending to Slack",
  sent: "Sent to Slack",
  uncertain:
    "Delivery unconfirmed — it may already be in Slack. Check the channel before sending again.",
  failed: "Slack delivery failed",
};
function deliveryLabel(delivery: Delivery) {
  if (delivery.status === "sent" && delivery.error)
    return "Slack message sent; result update pending";
  return deliveryCopy[delivery.status];
}
const emptyStatus: SlackConversationStatus = {
  destinations: [],
  deliveries: [],
};

type Props = {
  caseId: string;
  conversationId: string;
  conversationPending: boolean;
  onRefreshConversation: () => Promise<void>;
};

function useSlackDelivery(props: Props) {
  const { caseId, conversationId } = props;
  const [status, setStatus] = useState<SlackConversationStatus>(emptyStatus);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [operation, setOperation] = useState<string | null>(null);
  const action = useAsyncAction();
  const [redirecting, setRedirecting] = useState(false);
  const redirectStarted = useRef(false);
  const mounted = useRef(false);
  const identity = useRef(getRescueConversationIdentity());
  const requestVersion = useRef(0);
  const reading = useRef<Promise<void> | null>(null);
  const busy = useRef(false);
  const refresh = useRef(props.onRefreshConversation);
  refresh.current = props.onRefreshConversation;
  const current = useCallback(
    () =>
      mounted.current && identity.current === getRescueConversationIdentity(),
    [],
  );

  const read = useCallback(() => {
    if (reading.current) return reading.current;
    const version = requestVersion.current;
    const operation = getSlackConversationStatus(caseId, conversationId)
      .then((result) => {
        if (!current() || version !== requestVersion.current) return;
        setStatus(result);
        setLoaded(true);
        setError(null);
      })
      .catch((cause) => {
        if (current() && version === requestVersion.current)
          setError(
            cause instanceof Error
              ? cause.message
              : "Slack delivery status unavailable.",
          );
      })
      .finally(() => {
        reading.current = null;
      });
    reading.current = operation;
    return operation;
  }, [caseId, conversationId, current]);

  useEffect(() => {
    mounted.current = true;
    void read();
    const clearContext = () => {
      if (current()) return;
      requestVersion.current += 1;
      setStatus(emptyStatus);
      setLoaded(false);
      setError("Your account or workspace changed. Reopen the investigation.");
    };
    window.addEventListener("auth:changed", clearContext);
    window.addEventListener("storage", clearContext);
    return () => {
      mounted.current = false;
      window.removeEventListener("auth:changed", clearContext);
      window.removeEventListener("storage", clearContext);
    };
  }, [read, current]);

  const hasDelivery = status.deliveries.length > 0;
  const deliveryInProgress = status.deliveries.some(
    (delivery) =>
      ["queued", "sending"].includes(delivery.status) ||
      (delivery.status === "sent" && Boolean(delivery.error)),
  );
  useEffect(() => {
    if (!hasDelivery || (!props.conversationPending && !deliveryInProgress))
      return;
    let stopped = false;
    let timer: ReturnType<typeof setTimeout>;
    const poll = async () => {
      if (stopped || !current()) return;
      if (!busy.current) {
        await read();
        if (stopped || !current()) return;
        try {
          if (props.conversationPending) await refresh.current();
        } catch (cause) {
          if (current())
            setError(
              cause instanceof Error
                ? cause.message
                : "Investigation refresh unavailable. Reload and try again.",
            );
        }
      }
      if (!stopped && current())
        timer = setTimeout(() => {
          void poll();
        }, 5000);
    };
    timer = setTimeout(() => {
      void poll();
    }, 5000);
    return () => {
      stopped = true;
      clearTimeout(timer);
    };
  }, [
    hasDelivery,
    deliveryInProgress,
    props.conversationPending,
    current,
    read,
  ]);

  const run = (kind: "reload" | "send" | "retry" | "link", id = "") =>
    action.run(async () => {
      if (!current() || redirectStarted.current) return;
      busy.current = true;
      requestVersion.current += 1;
      setOperation(["retry", "link"].includes(kind) ? id : kind);
      setError(null);
      try {
        if (kind === "link") {
          const url = await startSlackConversationIdentity(id, caseId);
          if (!current()) return;
          window.location.assign(url);
          redirectStarted.current = true;
          setRedirecting(true);
          return;
        }
        if (kind !== "reload") {
          const delivery =
            kind === "send"
              ? await sendSlackConversation(caseId, conversationId, id)
              : await retrySlackConversation(caseId, conversationId, id);
          if (!current()) return;
          setStatus((previous) => ({
            ...previous,
            deliveries: [
              ...previous.deliveries.filter((item) => item.id !== delivery.id),
              delivery,
            ],
          }));
        }
        await reading.current;
        if (current()) await read();
      } catch (cause) {
        if (current())
          setError(
            cause instanceof Error
              ? cause.message
              : "Slack delivery could not be updated.",
          );
      } finally {
        if (!redirectStarted.current) {
          busy.current = false;
          if (current()) setOperation(null);
        }
      }
    });
  return {
    status,
    loaded,
    error,
    operation,
    pending: action.pending || redirecting,
    run,
  };
}

export function SlackConversationDelivery(props: Props) {
  return (
    <ScopedSlackConversationDelivery
      key={`${props.caseId}:${props.conversationId}`}
      {...props}
    />
  );
}

function ScopedSlackConversationDelivery(props: Props) {
  const { status, loaded, error, pending, operation, run } =
    useSlackDelivery(props);
  const [destinationId, setDestinationId] = useState("");
  const disclosure = useRef<HTMLDetailsElement>(null);
  const [returnedFromSlack] = useState(() => {
    if (
      window.location.pathname !==
      `/rescues/${encodeURIComponent(props.caseId)}`
    )
      return null;
    const value = new URLSearchParams(window.location.search).get("slack");
    return value === "connected" || value === "error" ? value : null;
  });
  useEffect(() => {
    if (returnedFromSlack && disclosure.current) disclosure.current.open = true;
  }, [returnedFromSlack]);
  useEffect(() => {
    if (error && disclosure.current) disclosure.current.open = true;
  }, [error]);
  const uncertain = status.deliveries.some(
    (delivery) => delivery.status === "uncertain",
  );
  const allowed = status.destinations.filter(
    (destination) =>
      destination.canSend &&
      !uncertain &&
      !status.deliveries.some(
        (delivery) => delivery.connectionId === destination.id,
      ),
  );
  const selected = allowed.find(
    (destination) => destination.id === destinationId,
  );
  return (
    <details className="rounded-xl border bg-muted/20 p-4" ref={disclosure}>
      <summary className="min-h-11 cursor-pointer py-3 text-sm font-medium focus-visible:ring-2 focus-visible:ring-ring">
        Continue in Slack
      </summary>
      <div className="space-y-4 pt-3">
        <p className="text-sm leading-6 text-muted-foreground">
          The question and result will be visible to members of this Slack
          channel. Only your linked Slack account can answer.
        </p>
        {returnedFromSlack === "error" ? (
          <p role="alert" className="text-sm text-destructive">
            Slack account linking wasn’t completed. Try connecting your account
            again below.
          </p>
        ) : null}
        {error ? (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        ) : null}
        {!loaded || error ? (
          <Button
            type="button"
            variant="outline"
            loading={pending}
            loadingLabel="Loading Slack status…"
            onClick={() => void run("reload")}
          >
            Reload Slack status
          </Button>
        ) : null}
        {loaded && status.destinations.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Connect Slack in Integrations to choose a channel.
          </p>
        ) : null}
        {status.destinations.some((destination) => !destination.canSend) ? (
          <p className="text-sm text-muted-foreground">
            Connect your Slack account below. If sending is still unavailable
            afterward, ask a workspace administrator to reconnect Slack in
            Integrations to grant messaging access.
          </p>
        ) : null}
        {status.destinations
          .filter((destination) => !destination.canSend)
          .map((destination) => (
            <Button
              key={destination.id}
              type="button"
              variant="outline"
              disabled={pending}
              loading={pending && operation === destination.id}
              loadingLabel="Opening Slack sign-in…"
              className="h-auto min-h-11 max-w-full whitespace-normal"
              onClick={() => void run("link", destination.id)}
            >
              Connect my Slack account · {destination.channelName}
            </Button>
          ))}
        {props.conversationPending && allowed.length > 0 ? (
          <div className="space-y-3">
            <ChoiceSelect
              aria-label="Slack destination"
              placeholder="Choose a Slack channel"
              value={destinationId}
              disabled={pending}
              onValueChange={setDestinationId}
              options={allowed.map((destination) => ({
                value: destination.id,
                label: `${destination.name} · ${destination.channelName}`,
              }))}
            />
            <Button
              type="button"
              className="h-auto min-h-11 max-w-full whitespace-normal"
              disabled={pending || !selected}
              loading={pending && operation === "send"}
              loadingLabel="Queueing Slack question…"
              onClick={() => {
                if (selected) void run("send", selected.id);
              }}
            >
              Send question to Slack
            </Button>
          </div>
        ) : null}
        <div className="space-y-3" aria-live="polite">
          {status.deliveries.map((delivery) => (
            <div key={delivery.id} className="space-y-2 rounded-lg border p-3">
              <p className="text-sm font-medium">{deliveryLabel(delivery)}</p>
              <p className="text-xs text-muted-foreground">
                {status.destinations.find(
                  (destination) => destination.id === delivery.connectionId,
                )?.channelName ?? "Connected Slack channel"}{" "}
                · Updated {new Date(delivery.updatedAt).toLocaleString()}
              </p>
              {delivery.error ? (
                <p className="break-words text-sm text-destructive">
                  {delivery.error}
                </p>
              ) : null}
              {delivery.status === "failed" &&
              status.destinations.some(
                (destination) =>
                  destination.id === delivery.connectionId &&
                  destination.canSend,
              ) ? (
                <Button
                  type="button"
                  variant="outline"
                  disabled={pending}
                  loading={pending && operation === delivery.id}
                  loadingLabel="Retrying delivery…"
                  onClick={() => void run("retry", delivery.id)}
                >
                  Retry Slack delivery
                </Button>
              ) : null}
            </div>
          ))}
        </div>
      </div>
    </details>
  );
}
